import STORAGE_KEYS from '../../../shared/constants/storage';
import { DEEPLINK_PARAMS } from '../../../shared/constants/deeplinkParams';
import { getStorage } from '../../../shared/utils/general.util';
import { logger } from '../../../shared/utils/logger';
import { ALLY_IDS, isAllyId, type AllyId } from '../../onboarding-v3/model/allyIds';
import {
	ALLY_CATALOG,
	hasDirectCampaignLanding,
} from '../../onboarding-v3/screens/allyCatalog';
import { INFO_SEEN_KEY, skipKey } from '../../onboarding-v3/model/walk';
import { mergeAllyIds } from '../../onboarding-v3/model/applyTransition';
import {
	ALLY_IDS_ANSWER_KEY,
	CAMPAIGN_OFFER_ANSWER_KEY,
	type AnswerValue,
	type CampaignOffer,
} from '../../onboarding-v3/model/flowSchema';

// Deep-link targets — las URLs de campaña de los aliados. Hay UN target por
// aliado del catálogo (ALLY_IDS), generado: dar de alta un aliado le crea su
// URL sin tocar rutas ni esta tabla. Un `?partner=` que no resuelva a un
// aliado real se ignora en silencio (el catálogo es el enum).
//
// El destino es SIEMPRE el host de /onboarding: `prestampAnswers` es el patch
// que se aplica al record post-auth, y el walk del host rutea al usuario por
// esas respuestas (goal → rama de aliado, con el grid ya resuelto). No hay
// rutas por aliado: la maquinaria de v2 murió con su funnel.

type Answer = { label: string; value: unknown };

export type OnboardingTargetId = `ally:${AllyId}`;

export type OnboardingTarget = {
	id: OnboardingTargetId;
	/** El aliado del target. Se declara al construirlo en vez de re-derivarse
	 * del id por string: quien necesita el aliado (el login brandeado) lo lee
	 * sin volver a parsear el prefijo `ally:`. */
	allyId: AllyId;
	/**
	 * Nodo del flow donde debe aterrizar la campaña. El id DEBE igualar la clave
	 * del flow JSON (mismo contrato que ROUTES.ALLIES_GRID).
	 *
	 * Existe porque el prestamp solo no alcanza para un usuario que YA completó
	 * su onboarding: el host planta a los completados en goals —el hub
	 * re-entrable— y solo se asienta en otro nodo si la URL lo nombra. Con el
	 * nodo, el host hace un fast-forward LEGAL hasta ahí; si el camino del
	 * usuario no lo cruza (los aliados con pregunta de rol propia, AG y Morada
	 * Uno), cae de vuelta a goals por su cuenta. Degradar es automático.
	 *
	 * Para el usuario NUEVO —el caso de esta campaña— este nodo ni se usa: su
	 * record no está completo, así que el host lo sienta en la frontera del walk
	 * (nombre → correo → teléfono) y el prestamp solo le ahorra los pasos que ya
	 * vienen contestados.
	 */
	landing: string;
	prestampAnswers: Record<string, Answer>;
	/** El rol que la URL de la campaña ya contestó (`?calc=` traducido por
	 * `Ally.campaignRole`), cuando el aliado declara uno y el slug es suyo. Vive
	 * en el target y no solo dentro de `prestampAnswers` para que guardarlo no
	 * tenga que volver a adivinar cuál de las respuestas era el rol. */
	campaignRole?: string;
	/** Qué vende el link, solo para un aliado con `Ally.annualOffer`: 'annual' si
	 * la URL trae `?offer=annual`, 'subscription' si no. Viaja también como
	 * respuesta (`campaign_offer`) dentro de `prestampAnswers`. */
	campaignOffer?: CampaignOffer;
	/** El identificador propio del aliado para este visitante (`?ref=`). Se lo
	 * devolvemos en su reporte por API para que cruce contra su base. */
	partnerRef?: string;
	/** Primer clic en el link (ms). Solo en el target leído del navegador y solo
	 * para un aliado cuya ventana cuenta desde el primer clic
	 * (`Ally.attributionWindowDays`): en los demás el registro se renueva con
	 * cada clic y su fecha no es la del primero. */
	firstClickAt?: number;
};

/** Primera pantalla REAL de la alianza: el grid se salta solo (ctx.allyPreselected
 * lo oculta) porque el prestamp ya trae el aliado. Exportado porque el host
 * decide con él si una URL de campaña o de login debe repetir este paso. */
export const ALLY_LANDING_NODE = 'ally_email';

/**
 * La apertura GENÉRICA del embudo ("Llegaste al lugar indicado", video de 60
 * segundos para conocer heru). Para un aliado del grid es la primera impresión
 * correcta; para quien llega por un link de campaña PAGADA no: ya vio el anuncio
 * del partner, ya decidió entrar, y meterle un intro de marca genérica es
 * gastarle el impulso por el que pagamos.
 *
 * Se estampa como VISTO en vez de ocultarse con `visibleWhen` porque el tronco
 * de registro (welcome/name/email/phone) es idéntico entre versiones del flow a
 * propósito —el experimento 50/50 debe variar el onboarding, no el registro— y
 * un `visibleWhen` nuevo lo haría divergir. El prestamp dice exactamente lo que
 * pasó: la campaña ya cubrió ese paso.
 */
const GENERIC_INTRO_NODE = 'welcome';

/**
 * La pregunta OPCIONAL de CURP del spine y el nodo al que su skip lleva (el
 * `next` del nodo; paridad con staticSkipTarget en admatticCampaign.test). La
 * campaña la oculta mientras dura (`campaignPrefilled`), pero ese flag se
 * apaga al conectar la cuenta del aliado y el walk de una SESIÓN NUEVA volvía a
 * frenar aquí: al repartidor de DiDi que ya aceptó T&Cs le reaparecían CURP,
 * goals, el grid, la pregunta de servicio, el correo y los T&Cs (D3 de t-076).
 * Estamparla como SALTADA dice lo que pasó —el link decidió por él— y el walk
 * la pasa de largo en todo resume; el CURP del programa se captura en el hub.
 */
const OPTIONAL_CURP_NODE = 'curp';
const OPTIONAL_CURP_SKIP_TARGET = 'goals';

const buildAllyTarget = (allyId: AllyId): OnboardingTarget => ({
	id: `ally:${allyId}`,
	allyId,
	landing: ALLY_LANDING_NODE,
	prestampAnswers: {
		// Solo el link de un aliado con entrada directa (hasDirectCampaignLanding)
		// se salta la apertura genérica. El link de un aliado del grid la conserva:
		// no hay un anuncio previo cuyo impulso cuidar.
		...(hasDirectCampaignLanding(ALLY_CATALOG[allyId])
			? {
					[INFO_SEEN_KEY(GENERIC_INTRO_NODE)]: {
						label: 'Intro genérica (cubierta por la campaña del aliado)',
						value: true,
					},
				}
			: {}),
		goal: {
			label: '¿Con qué te podemos ayudar?',
			value: 'ally_recommended',
		},
		// El host rutea la rama por `ally_selected` (el saves del grid).
		ally_selected: {
			label: '¿Con quién trabajas?',
			value: allyId,
		},
		// Slot canónico del perfil (la unión que lee resolveAllyIds). Lo escribe
		// el mirror del reducer al contestar el grid — que aquí NO se contesta,
		// se salta: sin estamparlo, el usuario que llega por link de aliado
		// quedaba fuera de su propio segmento.
		[ALLY_IDS_ANSWER_KEY]: {
			label: '¿Con quién trabajas?',
			value: [allyId],
		},
		// Vino por el link de su plataforma: preguntárselo sería pedirle un dato
		// que la URL ya dio. El aliado EXACTO vive en `onboarding_target`, así que
		// la granularidad no se pierde por colapsarlo a esta choice.
		discovery_source: {
			label: '¿Cómo conociste heru?',
			value: 'plataforma',
		},
		[skipKey(OPTIONAL_CURP_NODE)]: {
			label: 'CURP (opcional, saltado por la campaña del aliado)',
			value: OPTIONAL_CURP_SKIP_TARGET,
		},
		// Aliado con DOS servicios (DiDi): el link que reparte el aliado es el del
		// programa, así que la pregunta de intención (su nodo de ROL) llega
		// contestada; el nodo es campaign-owned (campaignPrefilled la oculta, como
		// al grid) y el flow la pasa de largo. Es la ÚNICA puerta por la que el
		// programa aplica sin que el usuario elija: toda otra entrada a DiDi sin
		// intención guardada pasa por la pregunta.
		...(ALLY_CATALOG[allyId].compliance?.intent && ALLY_CATALOG[allyId].roleAnswerKey
			? {
					[ALLY_CATALOG[allyId].roleAnswerKey]: {
						label: '¿Qué servicio buscas?',
						value: ALLY_CATALOG[allyId].compliance.intent.programChoice,
					},
				}
			: {}),
	},
});

export const ONBOARDING_TARGETS: Record<OnboardingTargetId, OnboardingTarget> =
	Object.fromEntries(
		ALLY_IDS.map((allyId) => [`ally:${allyId}`, buildAllyTarget(allyId)]),
	) as Record<OnboardingTargetId, OnboardingTarget>;

export const isOnboardingTargetId = (
	value: string | null | undefined,
): value is OnboardingTargetId =>
	!!value && value.startsWith('ally:') && isAllyId(value.slice('ally:'.length));

export const getOnboardingTarget = (
	value: string | null | undefined,
): OnboardingTarget | null =>
	isOnboardingTargetId(value) ? ONBOARDING_TARGETS[value] : null;

/**
 * Slugs de marketing que NO son el id del aliado. La normalización ya cubre las
 * variantes de puntuación y mayúsculas (`uber-eats`, `Uber_Eats`, `UBEREATS` →
 * uber_eats), así que aquí solo entra un nombre genuinamente distinto al id.
 */
const PARTNER_SLUG_ALIASES: Record<string, AllyId> = {
	ag: 'ag_enterprises',
	agstart: 'ag_enterprises',
};

const normalizeSlug = (slug: string): string =>
	slug.trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const ALLY_ID_BY_NORMALIZED_SLUG: Record<string, AllyId> = {
	...Object.fromEntries(ALLY_IDS.map((allyId) => [normalizeSlug(allyId), allyId])),
	...PARTNER_SLUG_ALIASES,
};

/** El aliado detrás de un `?partner=<slug>`, o null si el slug no es de nadie. */
export const resolvePartnerSlug = (
	slug: string | null | undefined,
): AllyId | null =>
	slug ? (ALLY_ID_BY_NORMALIZED_SLUG[normalizeSlug(slug)] ?? null) : null;

/**
 * El rol que la campaña trae contestado, para el aliado cuyo anuncio ya sabe el
 * segmento del visitante (`Ally.campaignRole`). El slug es el de la taxonomía
 * del ALIADO y el catálogo lo traduce a la choice de su nodo de rol.
 *
 * Un slug ausente, desconocido o de un aliado que no declara `campaignRole`
 * devuelve null y el usuario contesta la pregunta: la campaña puede ahorrarle
 * un paso, nunca inventarle una respuesta.
 */
const readCampaignRoleFromParams = (
	target: OnboardingTarget,
	params: URLSearchParams,
): string | null => {
	const config = ALLY_CATALOG[target.allyId].campaignRole;
	const slug = params.get(DEEPLINK_PARAMS.CALC);
	if (!config || !slug) return null;
	const role = config.choiceBySlug[normalizeSlug(slug)] ?? null;
	if (!role) logger.warn('[partner] segmento desconocido, se ignora', slug);
	return role;
};

/** El target con el rol de la campaña estampado como una respuesta más. Es la
 * ÚNICA forma de meter el rol al prestamp: así el camino del boot y el de la
 * rehidratación desde storage arman exactamente el mismo patch. */
const withCampaignRole = (
	target: OnboardingTarget,
	role: string | null | undefined,
): OnboardingTarget => {
	const ally = ALLY_CATALOG[target.allyId];
	// La llave es `roleAnswerKey`, la MISMA que lee resolveAllyRoleAnswer. Sin
	// nodo de rol no hay dónde estampar y el prestamp se cae solo.
	if (!ally.campaignRole || !ally.roleAnswerKey || !role) return target;
	return {
		...target,
		campaignRole: role,
		prestampAnswers: {
			...target.prestampAnswers,
			[ally.roleAnswerKey]: { label: ally.campaignRole.label, value: role },
		},
	};
};

/** Qué vende la URL, solo para un aliado con `Ally.annualOffer`: 'annual' si el
 * link trae `?offer=annual`, 'subscription' si no. null para el resto. */
const readCampaignOfferFromParams = (
	target: OnboardingTarget,
	params: URLSearchParams,
): CampaignOffer | null => {
	if (!ALLY_CATALOG[target.allyId].annualOffer) return null;
	const offer = params.get(DEEPLINK_PARAMS.PARTNER_OFFER)?.trim().toLowerCase();
	return offer === 'annual' ? 'annual' : 'subscription';
};

/** El target con la oferta estampada como respuesta (`campaign_offer`). Para un
 * aliado con `annualOffer` se estampa SIEMPRE, también 'subscription': así el
 * link del plan pisa en el record a un link anual tocado antes. */
const withCampaignOffer = (
	target: OnboardingTarget,
	offer: CampaignOffer | null | undefined,
): OnboardingTarget => {
	if (!ALLY_CATALOG[target.allyId].annualOffer) return target;
	const value = offer ?? 'subscription';
	return {
		...target,
		campaignOffer: value,
		prestampAnswers: {
			...target.prestampAnswers,
			[CAMPAIGN_OFFER_ANSWER_KEY]: { label: 'Oferta del link de campaña', value },
		},
	};
};

/** El formato que se le promete al aliado para `?ref=`: corto y sin caracteres
 * que haya que escapar. Uno fuera de formato se ignora; nunca se trunca, porque
 * un id recortado ya no cruza contra nada en su base. */
const PARTNER_REF_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;

const readPartnerRefFromParams = (params: URLSearchParams): string | null => {
	const ref = params.get(DEEPLINK_PARAMS.PARTNER_REF)?.trim();
	if (!ref) return null;
	if (PARTNER_REF_PATTERN.test(ref)) return ref;
	logger.warn('[partner] ref fuera de formato, se ignora', ref);
	return null;
};

const withPartnerRef = (
	target: OnboardingTarget,
	ref: string | null | undefined,
): OnboardingTarget => (ref ? { ...target, partnerRef: ref } : target);

/**
 * El target de campaña que nombra una URL, en sus DOS formas: `?partner=<aliado>`
 * (la que se reparte hoy) y `?onboarding_target=ally:<id>` (la vieja de Zubale,
 * viva en comunicaciones ya impresas). Una sola lectura para que ningún borde
 * entienda solo la mitad de los links en circulación.
 */
export const readCampaignTargetFromParams = (
	params: URLSearchParams,
): OnboardingTarget | null => {
	const allyId = resolvePartnerSlug(params.get(DEEPLINK_PARAMS.PARTNER));
	const target = allyId
		? ONBOARDING_TARGETS[`ally:${allyId}`]
		: getOnboardingTarget(params.get(DEEPLINK_PARAMS.ONBOARDING_TARGET));
	if (!target) return null;
	return withPartnerRef(
		withCampaignOffer(
			withCampaignRole(target, readCampaignRoleFromParams(target, params)),
			readCampaignOfferFromParams(target, params),
		),
		readPartnerRefFromParams(params),
	);
};

/**
 * El target vive en localStorage (vía `getStorage`), NO en sessionStorage.
 *
 * sessionStorage es por-pestaña, y el camino real del usuario de un aliado casi
 * nunca cabe en una: entra por el link del aliado, pide su OTP, y abre el SMS o
 * el correo — que en móvil casi siempre estrena pestaña. Ahí sessionStorage
 * nace vacío, el target se pierde y el usuario aterriza en goals, que es
 * justamente lo que este deep-link existe para evitar.
 *
 * El precio de sobrevivir a la pestaña es sobrevivir demasiado, así que el
 * registro CADUCA: quien tocó el link del aliado y se registra por su cuenta
 * mucho después no debe caer en el flujo del aliado. Por omisión son 7 días
 * desde el ÚLTIMO clic (cada clic renueva la ventana). El aliado que pacta su
 * propia ventana (`Ally.attributionWindowDays`) la cuenta desde el PRIMER clic,
 * como lo dice el contrato de Calcu.mx.
 */
const DEFAULT_ATTRIBUTION_WINDOW_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

const countsFromFirstClick = (allyId: AllyId): boolean =>
	ALLY_CATALOG[allyId].attributionWindowDays !== undefined;

const isWithinAttributionWindow = (
	target: OnboardingTarget,
	firstClickAt: number,
): boolean =>
	Date.now() - firstClickAt <=
	(ALLY_CATALOG[target.allyId].attributionWindowDays ??
		DEFAULT_ATTRIBUTION_WINDOW_DAYS) *
		DAY_MS;

/** `savedAt` es desde donde corre la ventana: el primer clic si el aliado la
 * cuenta así (countsFromFirstClick), el último si no. */
type StoredTarget = {
	id: string;
	savedAt: number;
	role?: string;
	ref?: string;
	offer?: CampaignOffer;
};

/** El registro guardado con su target, solo si sigue dentro de la ventana de
 * SU aliado. Uno vencido se borra. */
const readLiveStoredTarget = (): {
	stored: StoredTarget;
	target: OnboardingTarget;
} | null => {
	if (typeof window === 'undefined') return null;
	try {
		const raw = getStorage().getItem(STORAGE_KEYS.ONBOARDING_TARGET);
		if (!raw) return null;
		const stored = JSON.parse(raw) as StoredTarget;
		const target = getOnboardingTarget(stored.id);
		if (!target) {
			logger.warn('[partner] campaña guardada desconocida', stored.id);
			return null;
		}
		if (!isWithinAttributionWindow(target, stored.savedAt)) {
			logger.info('[partner] campaña vencida, se descarta', stored.id);
			clearOnboardingTarget();
			return null;
		}
		return { stored, target };
	} catch (error) {
		logger.warn('[partner] no se pudo leer la campaña guardada', error);
		return null;
	}
};

export const readOnboardingTarget = (): OnboardingTarget | null => {
	const live = readLiveStoredTarget();
	if (!live) return null;
	const target = withPartnerRef(
		withCampaignOffer(
			withCampaignRole(live.target, live.stored.role),
			live.stored.offer,
		),
		live.stored.ref,
	);
	return countsFromFirstClick(target.allyId)
		? { ...target, firstClickAt: live.stored.savedAt }
		: target;
};

/** Guarda el target COMPLETO (con el rol que la URL ya contestó). Recibe el
 * objeto y no el id porque el rol viaja con él: pasar solo el id perdía, en
 * silencio, la mitad de lo que el link traía.
 *
 * Para un aliado que cuenta desde el primer clic, si ya hay un target vigente
 * suyo se conserva ese primer clic y solo se actualizan el rol y el `ref` que
 * este clic traiga. En cualquier otro caso se reemplaza completo. */
export const writeOnboardingTarget = (
	target: OnboardingTarget | null | undefined,
): void => {
	if (typeof window === 'undefined' || !target) return;
	const previous = countsFromFirstClick(target.allyId)
		? readLiveStoredTarget()
		: null;
	const firstClick =
		previous?.target.id === target.id ? previous.stored : undefined;
	const role = target.campaignRole ?? firstClick?.role;
	const ref = target.partnerRef ?? firstClick?.ref;
	// La oferta NO se hereda del primer clic: cada link dice lo que vende, y el
	// último que tocó el usuario es el que ve.
	const offer = target.campaignOffer;
	try {
		const stored: StoredTarget = {
			id: target.id,
			savedAt: firstClick?.savedAt ?? Date.now(),
			...(role ? { role } : {}),
			...(ref ? { ref } : {}),
			...(offer ? { offer } : {}),
		};
		getStorage().setItem(
			STORAGE_KEYS.ONBOARDING_TARGET,
			JSON.stringify(stored),
		);
		logger.info('[partner] campaña guardada', target.id, {
			rol: role ?? null,
			ref: ref ?? null,
			oferta: offer ?? null,
			primerClic: new Date(stored.savedAt).toISOString(),
		});
	} catch (error) {
		// Storage bloqueado — el deep-link no funcionará en este navegador, pero el
		// usuario sigue pudiendo hacer el onboarding normal.
		logger.warn('[partner] no se pudo guardar la campaña', target.id, error);
	}
};

export const clearOnboardingTarget = (): void => {
	if (typeof window === 'undefined') return;
	try {
		getStorage().removeItem(STORAGE_KEYS.ONBOARDING_TARGET);
	} catch {
		// noop
	}
};

/**
 * `?partner=<slug>` colgado de CUALQUIER URL (`/?partner=zubale`,
 * `/plans?partner=ag`) — la forma que reparten los aliados en sus campañas.
 *
 * Se captura en el boot (main.tsx) y no en un hook por la misma razón que los
 * UTMs: el visitante anónimo que abre `/?partner=zubale` rebota a /login por el
 * gate de sesión, y ese rebote se lleva el query string. Al boot el param
 * todavía existe; en el primer render de una ruta, ya no.
 */
export const capturePartnerFromUrl = (): void => {
	if (typeof window === 'undefined') return;
	const params = new URLSearchParams(window.location.search);
	const slug = params.get(DEEPLINK_PARAMS.PARTNER);
	const target = readCampaignTargetFromParams(params);
	if (!target) {
		if (slug) logger.warn('[partner] slug desconocido, se ignora', slug);
		return;
	}
	logger.info('[partner] campaña capturada en el boot', {
		slug,
		target: target.id,
		rol: target.campaignRole ?? null,
		oferta: target.campaignOffer ?? null,
		url: window.location.pathname + window.location.search,
	});
	writeOnboardingTarget(target);
};

// El PATCH del pre-estampado. `onboarding_target` responde la pregunta
// implícita "¿de dónde llegó este usuario?" — lo leen las queries de analítica
// y ctx.allyPreselected (que exige target + aliado para ocultar el grid).
//
// `ally_ids` se estampa como la UNIÓN con lo que el record ya declaraba: un
// usuario de Doctoralia que toca el link de Calcu.mx suma Calcu.mx y conserva
// Doctoralia. El PATCH reemplaza la llave completa, así que mandar solo el
// aliado de la campaña lo sacaba en silencio de su segmento anterior.
export const buildOnboardingTargetPrestamp = (
	target: OnboardingTarget,
	recordAllyIds?: AnswerValue,
): { answers: Record<string, Answer> } => ({
	answers: {
		...target.prestampAnswers,
		[ALLY_IDS_ANSWER_KEY]: {
			...target.prestampAnswers[ALLY_IDS_ANSWER_KEY],
			value: mergeAllyIds(recordAllyIds, target.allyId),
		},
		onboarding_target: {
			label: 'Deep-link target',
			value: target.id,
		},
		// El id propio del aliado viaja con la campaña y no en el alta del
		// perfil: este PATCH lo escribe el host en el mismo momento en que liga
		// la organización del aliado, sin importar si la cuenta nació por
		// celular, correo o Google. El reporte por API del aliado lo lee de aquí.
		...(target.partnerRef
			? {
					partner_ref: {
						label: 'Id del aliado para este visitante',
						value: target.partnerRef,
					},
				}
			: {}),
		...(target.firstClickAt !== undefined
			? {
					partner_first_click_at: {
						label: 'Primer clic en el link del aliado',
						value: new Date(target.firstClickAt).toISOString(),
					},
				}
			: {}),
	},
});

/**
 * El aliado de la campaña que trajo a este usuario, mirando LAS DOS fuentes: la
 * ya confirmada (`ally_selected` del record) y la que todavía está en vuelo (el
 * target capturado en el boot, que el host estampa al hidratar).
 *
 * Las dos porque el prestamp viaja por un PATCH: entre que el usuario aterriza y
 * que el server confirma el record hay una ventana, y en esa ventana el ctx del
 * host no sabría que viene de una campaña. Para `campaignPrefilled` daba igual
 * —los nodos que apaga vienen más adelante en el walk— pero la entrada directa
 * se decide en el PRIMER nodo, así que ahí la ventana sí se ve.
 */
export const resolveCampaignAllyId = (
	stampedAllyId: string | null | undefined,
): AllyId | null => {
	if (isAllyId(stampedAllyId)) return stampedAllyId;
	const pending = readOnboardingTarget();
	if (!pending) return null;
	const fromTarget = pending.id.slice('ally:'.length);
	return isAllyId(fromTarget) ? fromTarget : null;
};

/**
 * ¿Este usuario llegó por el link de ESTE aliado? Lo dice el target que la
 * campaña estampó en el record o, en la ventana antes de que el server lo
 * confirme, el que el boot guardó.
 *
 * Separa al que tocó el link de Calcu.mx del que eligió Calcu.mx en el grid:
 * los dos terminan con el mismo `ally_selected`, pero solo el primero entra
 * directo a la alianza y pierde el «atrás» al grid.
 */
export const arrivedByAllyLink = (
	allyId: AllyId,
	stampedTarget: string | null | undefined,
): boolean => {
	const targetId: OnboardingTargetId = `ally:${allyId}`;
	return stampedTarget === targetId || readOnboardingTarget()?.id === targetId;
};

/**
 * El rol que la campaña ya contestó, para las superficies ANÓNIMAS que quieren
 * hablarle al segmento y no a la promesa genérica (el login co-brandeado).
 *
 * Se lee del target guardado y no de la URL porque para el tráfico que la
 * campaña busca la URL ya no existe: el gate de sesión rebotó al visitante a
 * /login y el rebote se llevó el query string. El boot lo guardó antes.
 */
export const resolveCampaignRole = (): string | null =>
	readOnboardingTarget()?.campaignRole ?? null;

// ── Entry intent (`?intent=`) ──
//
// What the visitor came for, said by the link itself (`?intent=facturar`). It
// lives in its OWN slot, in the browser and on the record (`entry_intent`),
// and never touches `onboarding_target`: that key is the ally attribution UME
// reads for signups and commissions (`onboarding_target LIKE 'ally:%'`), so an
// ally user who taps an invoicing link keeps their ally. When an ally campaign
// owns the onboarding the ally keeps the offer and the intent is only recorded.
//
// Captured at boot for the same reason as `?partner=` (the login bounce drops
// the query string) and kept for the default attribution window. The flag
// gate (`onboarding-solo-facturar`) lives in its readers, the host and
// useEntryIntentDeeplink: with the flag off nothing reads it.

export const ENTRY_INTENT_ANSWER_KEY = 'entry_intent';

/** Entry intent → the goal it answers. The goal routes the onboarding walk. */
const ENTRY_INTENT_GOALS = {
	facturar: 'invoicing_only',
} as const;

export type EntryIntent = keyof typeof ENTRY_INTENT_GOALS;

const isEntryIntent = (value: unknown): value is EntryIntent =>
	typeof value === 'string' &&
	Object.prototype.hasOwnProperty.call(ENTRY_INTENT_GOALS, value);

export const readEntryIntentFromParams = (
	params: URLSearchParams,
): EntryIntent | null => {
	const slug = params.get(DEEPLINK_PARAMS.INTENT)?.trim().toLowerCase();
	if (!slug) return null;
	if (isEntryIntent(slug)) return slug;
	logger.warn('[intent] unknown entry intent, ignored', slug);
	return null;
};

type StoredEntryIntent = { intent: EntryIntent; savedAt: number };

export const writeEntryIntent = (intent: EntryIntent): void => {
	if (typeof window === 'undefined') return;
	try {
		const stored: StoredEntryIntent = { intent, savedAt: Date.now() };
		getStorage().setItem(STORAGE_KEYS.ENTRY_INTENT, JSON.stringify(stored));
	} catch (error) {
		logger.warn('[intent] could not store the entry intent', intent, error);
	}
};

export const clearEntryIntent = (): void => {
	if (typeof window === 'undefined') return;
	try {
		getStorage().removeItem(STORAGE_KEYS.ENTRY_INTENT);
	} catch {
		// noop
	}
};

/** The stored entry intent, only while inside its window; an expired one is cleared. */
export const readEntryIntent = (): EntryIntent | null => {
	if (typeof window === 'undefined') return null;
	try {
		const raw = getStorage().getItem(STORAGE_KEYS.ENTRY_INTENT);
		if (!raw) return null;
		const stored = JSON.parse(raw) as Partial<StoredEntryIntent>;
		const fresh =
			typeof stored.savedAt === 'number' &&
			Date.now() - stored.savedAt <= DEFAULT_ATTRIBUTION_WINDOW_DAYS * DAY_MS;
		if (isEntryIntent(stored.intent) && fresh) return stored.intent;
		clearEntryIntent();
		return null;
	} catch (error) {
		logger.warn('[intent] could not read the stored entry intent', error);
		return null;
	}
};

export const captureEntryIntentFromUrl = (): void => {
	if (typeof window === 'undefined') return;
	const intent = readEntryIntentFromParams(new URLSearchParams(window.location.search));
	if (intent) writeEntryIntent(intent);
};

/**
 * The record patch of an entry intent. `entry_intent` is always stamped: it is
 * the measurement of how the user came in. The goal is answered, and the
 * optional CURP skipped, only with `answersGoal`; the host passes false while
 * an ally campaign owns the onboarding (the ally keeps its offer) and for a
 * completed user (who lands on the goals hub and picks the choice there).
 */
export const buildEntryIntentPrestamp = (
	intent: EntryIntent,
	{ answersGoal }: { answersGoal: boolean },
): { answers: Record<string, Answer> } => ({
	answers: {
		[ENTRY_INTENT_ANSWER_KEY]: { label: 'Entry intent (deep link)', value: intent },
		...(answersGoal
			? {
					goal: {
						label: '¿Con qué te podemos ayudar?',
						value: ENTRY_INTENT_GOALS[intent],
					},
					[skipKey(OPTIONAL_CURP_NODE)]: {
						label: 'CURP (opcional, saltado por la entrada de facturación)',
						value: OPTIONAL_CURP_SKIP_TARGET,
					},
				}
			: {}),
	},
});
